import { Box, Stack, Tooltip, Typography } from '@mui/material'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { SensorGlyph } from './EmptyList'
import useTranslation from '@/hooks/useTranslation'
import { readingFreshness, type ReadingFreshness } from './sensor-freshness'
import { useRuleCopy } from './use-rule-copy'

export interface SensorCardRow {
  id: number
  active: boolean
  io: string
  decoderRef: string
  controller?: string
  measuredFlow: number
  usage: number
  volumeLastTs: number
}

interface SensorCardsProps {
  rows: SensorCardRow[]
  isDark: boolean
  /** Only a control hub has more than one controller to name. */
  showController?: boolean
  /**
   * The site's own timezone. Readings are stamped in controller-local time, so
   * without this an operator abroad reads a healthy sensor as hours stale.
   */
  timeZone?: string | null
  /** Converts the stored metric flow for display. */
  formatFlow: (value: number) => string
  flowUnit: string
}

/**
 * The rule's flow sensors as cards, in the same three-band shape as the valves:
 * what it is and whether it counts, then its numbers.
 *
 * A sensor has no state of its own to report and no error field — its whole
 * job is the two readings — so the measures carry the card rather than sitting
 * under a status.
 */
const SensorCards = ({
  rows,
  isDark,
  showController,
  timeZone,
  formatFlow,
  flowUnit,
}: SensorCardsProps) => {
  const copy = useRuleCopy()
  const { t } = useTranslation()

  // "12 min" / "3 h" / "2 d", or "just now" for a controller clock that reads
  // ahead of ours. The sentence around it is a separate key, so a translation
  // can put the age wherever its grammar needs it.
  const formatAge = ({ amount, unit }: NonNullable<ReadingFreshness['age']>) => {
    if (unit === 'now') return t('flowRulePanelReadingJustNow')
    const key =
      unit === 'min'
        ? 'flowRulePanelReadingMinutes'
        : unit === 'h'
          ? 'flowRulePanelReadingHours'
          : 'flowRulePanelReadingDays'
    return t(key, { count: amount })
  }
  const ink = isDark ? '#eaf1f3' : '#1d2939'
  const muted = isDark ? '#8fa3ad' : '#667085'
  const line = isDark ? 'rgba(255,255,255,0.09)' : '#e4eaea'
  const surface = isDark ? '#0a2836' : '#ffffff'
  const wellBg = isDark ? 'rgba(255,255,255,0.035)' : '#f7fafa'
  const accentOn = isDark ? '#34d399' : '#0f9d58'
  const accentOff = isDark ? '#5b7683' : '#98a5ad'
  const warn = isDark ? '#fbbf24' : '#b45309'

  const measure = (label: string, value: React.ReactNode, tip: string) => (
    <Tooltip title={tip} placement="top">
      <Stack sx={{ gap: '1px', minWidth: 0, cursor: 'help' }}>
        <Typography
          sx={{
            fontSize: '9.5px',
            fontWeight: 700,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            color: muted,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: '19px',
            fontWeight: 700,
            lineHeight: 1.15,
            color: ink,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </Typography>
      </Stack>
    </Tooltip>
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        gap: '10px',
        p: '12px',
      }}
    >
      {rows.map((row) => {
        const accent = row.active ? accentOn : accentOff
        // Nothing else in the product notices a sensor that quietly stopped
        // recording: the rule and valve faults only speak up once irrigation
        // runs against it.
        const freshness = readingFreshness(row.volumeLastTs, timeZone)

        return (
          <Box
            key={row.id}
            sx={{
              position: 'relative',
              flex: '1 1 260px',
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid',
              borderColor: line,
              borderRadius: '12px',
              backgroundColor: surface,
              overflow: 'hidden',
              // An ignored sensor is present but plainly not contributing.
              opacity: row.active ? 1 : 0.6,
              transition: 'box-shadow 140ms ease',
              '&:hover': {
                boxShadow: isDark
                  ? '0 6px 18px rgba(0,0,0,0.45)'
                  : '0 6px 18px rgba(16,24,40,0.10)',
              },
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '3px',
                backgroundColor: accent,
              }}
            />

            <Stack
              direction="row"
              sx={{
                alignItems: 'flex-start',
                gap: '9px',
                pl: '14px',
                pr: '11px',
                pt: '11px',
                pb: '10px',
              }}
            >
              <Box
                sx={{
                  color: accent,
                  display: 'inline-flex',
                  flexShrink: 0,
                  mt: '1px',
                  '& svg': { width: 20, height: 20 },
                }}
              >
                <SensorGlyph />
              </Box>

              <Stack sx={{ minWidth: 0, flex: 1, gap: '2px' }}>
                <Typography
                  sx={{
                    fontSize: '13.5px',
                    fontWeight: 700,
                    color: ink,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row.io || row.decoderRef || '—'}
                </Typography>
                <Stack
                  direction="row"
                  sx={{ alignItems: 'baseline', gap: '6px', minWidth: 0 }}
                >
                  {row.decoderRef && (
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: muted,
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.decoderRef}
                    </Typography>
                  )}
                  {showController && row.controller && (
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: muted,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.controller}
                    </Typography>
                  )}
                </Stack>
              </Stack>

              <Tooltip
                title={
                  row.active
                    ? copy.sensorCountedTip
                    : copy.sensorIgnoredTip
                }
                placement="top"
              >
                <Typography
                  sx={{
                    flexShrink: 0,
                    cursor: 'help',
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: accent,
                    backgroundColor: row.active
                      ? `${accent}1f`
                      : isDark
                        ? 'rgba(255,255,255,0.06)'
                        : 'rgba(16,24,40,0.05)',
                    borderRadius: '11px',
                    px: '8px',
                    py: '3px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row.active ? copy.sensorInUse : copy.sensorNotInUse}
                </Typography>
              </Tooltip>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                columnGap: '10px',
                mr: '11px',
                ml: '14px',
                mb: '11px',
                px: '10px',
                py: '9px',
                borderRadius: '9px',
                backgroundColor: wellBg,
              }}
            >
              {measure(
                copy.flow,
                <>
                  {formatFlow(Number(row.measuredFlow ?? 0))}
                  <Box
                    component="span"
                    sx={{ fontSize: '11px', fontWeight: 500, color: muted }}
                  >
                    {' '}
                    {flowUnit}
                  </Box>
                </>,
                copy.tips.sensorFlowNow,
              )}
              {measure(
                copy.usage24h,
                <>
                  {(Number(row.usage ?? 0) / 1000).toFixed(1)}
                  <Box
                    component="span"
                    sx={{ fontSize: '11px', fontWeight: 500, color: muted }}
                  >
                    {' '}
                    m³
                  </Box>
                </>,
                copy.tips.sensorUsage24h,
              )}
            </Box>

            {freshness.age && (
              <Tooltip
                title={
                  freshness.stale
                    ? copy.tips.sensorReadingStale
                    : copy.tips.sensorReadingFresh
                }
                placement="top"
              >
                <Stack
                  direction="row"
                  sx={{
                    alignItems: 'center',
                    gap: '5px',
                    px: '14px',
                    pb: '11px',
                    mt: '-3px',
                    cursor: 'help',
                    color: freshness.stale ? warn : muted,
                  }}
                >
                  {freshness.stale ? (
                    <WarningAmberOutlinedIcon sx={{ fontSize: 13 }} />
                  ) : (
                    <ScheduleOutlinedIcon sx={{ fontSize: 13 }} />
                  )}
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: freshness.stale ? 650 : 500,
                    }}
                  >
                    {t(
                      freshness.stale
                        ? 'flowRulePanelNoReadingFor'
                        : 'flowRulePanelLastReading',
                      { age: formatAge(freshness.age) },
                    )}
                  </Typography>
                </Stack>
              </Tooltip>
            )}
          </Box>
        )
      })}
    </Box>
  )
}

export default SensorCards
